import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Code, Sparkles } from 'lucide-react';
import { useApp } from '@/store/AppContext';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import apiClient from '@/api/apiClient';
import { mapApiImageToGenerated } from '@/components/shared/gallery/utils';
import { GeneratedImage } from '@/types';
import { useTranslation } from '@/i18n/useTranslation';
import actionPhotoImage from '@/img/dashboard/action-photo.png';
import mockupImage from '@/img/dashboard/mockup.png';
import packshotStudioImage from '@/img/dashboard/packshot-studio.png';
import actionPackshotImage from '@/img/dashboard/action-packshot.png';

export default function DashboardPage() {
  const { images, setAllImages, isAuthenticated, user, setActiveModule } = useApp();
  const hasLoadedRef = useRef(false);
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  const quickActions = [
    {
      title: t('dashboard.quickPhoto'),
      desc: t('dashboard.quickPhotoDesc'),
      image: actionPhotoImage,
      link: '/create?imageStyle=photo&sceneType=action&aspectRatio=16:9',
      module: 'photo' as const,
    },
    {
      title: t('dashboard.quickMockup'),
      desc: t('dashboard.quickMockupDesc'),
      image: mockupImage,
      link: '/create?imageStyle=photo&sceneType=mockup&device=iphone&aspectRatio=16:9',
      module: 'photo' as const,
    },
    {
      title: t('dashboard.quickPackshot'),
      desc: t('dashboard.quickPackshotDesc'),
      image: packshotStudioImage,
      link: '/create?imageStyle=packshot&sceneType=product_packshot&packshotStyle=studio&aspectRatio=1:1',
      module: 'packshot' as const,
    },
    {
      title: t('dashboard.quickArt'),
      desc: t('dashboard.quickArtDesc'),
      image: actionPackshotImage,
      link: '/create?imageStyle=packshot&sceneType=product_packshot&packshotStyle=auto&location=outdoor&aspectRatio=16:9',
      module: 'packshot' as const,
    },
  ];
  const [loadingImages, setLoadingImages] = useState(true);

  // Load user's images on mount (only once per mount)
  useEffect(() => {
    // Always load if images.length is 0 (e.g., after hard refresh)
    const shouldLoad = !hasLoadedRef.current || images.length === 0;
    
    if (!shouldLoad) {
      setLoadingImages(false);
      return;
    }
    
    hasLoadedRef.current = true;
    let mounted = true;
    
    const loadImages = async () => {
      try {
        const response = await apiClient.getImages();
        
        if (!mounted) return; // Prevent state update if unmounted
        
        if (response.success && response.images && response.images.length > 0) {
          const loadedImages: GeneratedImage[] = response.images.map(mapApiImageToGenerated);
          
          setAllImages(loadedImages);
        } else {
          setAllImages([]);
        }
      } catch (error) {
        console.error('[Dashboard] Failed to load images:', error);
        if (mounted) setAllImages([]);
      } finally {
        if (mounted) setLoadingImages(false);
      }
    };
    
    loadImages();
    
    return () => {
      mounted = false;
    };
  }, [setAllImages]); // Add setAllImages to deps (now memoized)

  const recent = useMemo(() => {
    return (images || []).slice(0, 6);
  }, [images]);

  return (
    <div className="h-full w-full bg-bg-main overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-20 md:pb-8">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary mb-1">
              {isAuthenticated && user ? t('dashboard.hello', { name: user.first_name || t('dashboard.there') }) : t('dashboard.title')}
            </h1>
            <p className="text-sm text-text-secondary">{t('dashboard.subtitle')}</p>
          </div>
        </div>

        {/* Make a quick image */}
        <div className="mb-10">
          <div className="text-xs uppercase tracking-wider text-text-secondary mb-4">{t('dashboard.quickActions')}</div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => {
                    if (item.module) {
                      setActiveModule(item.module);
                    }
                    navigate(item.link);
                  }}
                  className="text-left bg-bg-panel border border-border rounded-2xl overflow-hidden hover:bg-bg-control transition-colors group"
                >
                  <div className="aspect-video w-full overflow-hidden bg-bg-control">
                    <img
                      src={item.image}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="px-4 py-4">
                    <div className="text-sm font-semibold text-text-primary mb-1">{item.title}</div>
                    <div className="text-xs text-text-secondary">{item.desc}</div>
                  </div>
                </button>
            ))}
          </div>
        </div>

        {/* Recent */}
        {loadingImages ? (
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary mb-4">{t('dashboard.recentImages')}</div>
            {/* Mobile: Horizontal scroll carousel */}
            <div className="md:hidden overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide">
              <div className="flex gap-4 pr-4" style={{ width: 'max-content' }}>
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="bg-bg-panel border border-border rounded-2xl overflow-hidden flex-shrink-0" style={{ width: 'calc(50vw - 1.5rem)' }}>
                    <div className="aspect-square bg-bg-control animate-pulse" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-bg-control rounded w-3/4 animate-pulse" />
                      <div className="h-3 bg-bg-control rounded w-1/2 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Desktop: Grid */}
            <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="bg-bg-panel border border-border rounded-2xl overflow-hidden">
                  <div className="aspect-square bg-bg-control animate-pulse" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-bg-control rounded w-3/4 animate-pulse" />
                    <div className="h-3 bg-bg-control rounded w-1/2 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : recent.length > 0 ? (
          <div>
            <div className="text-xs uppercase tracking-wider text-text-secondary mb-4">{t('dashboard.recentImages')}</div>
            {/* Mobile: Horizontal scroll carousel */}
            <div className="md:hidden overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide">
              <div className="flex gap-4 pr-4" style={{ width: 'max-content' }}>
                {recent.map((img) => (
                  <button
                    key={img.id || img.filename || img.timestamp}
                    onClick={() => navigate(`/browse/${img.id}`, { state: { forceSingle: true } })}
                    className="bg-bg-panel border border-border rounded-2xl overflow-hidden hover:border-accent/50 hover:bg-bg-control transition-all text-left group flex-shrink-0"
                    style={{ width: 'calc(50vw - 1.5rem)' }}
                  >
                    <div className="aspect-square bg-bg-control overflow-hidden">
                      <img src={img.url} alt={img.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-3">
                      <div className="text-[11px] text-text-primary truncate group-hover:text-accent transition-colors">{img.filename}</div>
                      <div className="text-[10px] text-text-secondary">
                        {new Date(img.timestamp).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* Desktop: Grid */}
            <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {recent.map((img) => (
                <button
                  key={img.id || img.filename || img.timestamp}
                  onClick={() => navigate(`/browse/${img.id}`, { state: { forceSingle: true } })}
                  className="bg-bg-panel border border-border rounded-2xl overflow-hidden hover:border-accent/50 hover:bg-bg-control transition-all text-left group"
                >
                  <div className="aspect-square bg-bg-control overflow-hidden">
                    <img src={img.url} alt={img.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-3">
                    <div className="text-[11px] text-text-primary truncate group-hover:text-accent transition-colors">{img.filename}</div>
                    <div className="text-[10px] text-text-secondary">
                      {new Date(img.timestamp).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Banners */}
        <div className="mt-10">
          <div className="text-xs uppercase tracking-wider text-text-secondary mb-4">{t('dashboard.customSolutions.title')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="https://squares.agency"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-bg-panel border border-border rounded-2xl px-6 py-5 hover:bg-bg-control hover:border-accent/50 transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-colors">
                <Code className="w-6 h-6 text-bg-main group-hover:text-bg-main" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-text-primary mb-2 group-hover:text-accent transition-colors">
                  {t('dashboard.customSolutions.dev.title')}
                </div>
                <div className="text-xs text-text-secondary leading-relaxed mb-2">
                  {t('dashboard.customSolutions.dev.desc')}
                </div>
                <div className="text-xs text-accent font-medium">
                  {t('dashboard.customSolutions.dev.link')}
                </div>
              </div>
            </div>
          </a>
          <a
            href="https://2rep.studio"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-bg-panel border border-border rounded-2xl px-6 py-5 hover:bg-bg-control hover:border-accent/50 transition-all group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-colors">
                <Sparkles className="w-6 h-6 text-bg-main group-hover:text-bg-main" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-text-primary mb-2 group-hover:text-accent transition-colors">
                  {t('dashboard.customSolutions.design.title')}
                </div>
                <div className="text-xs text-text-secondary leading-relaxed mb-2">
                  {t('dashboard.customSolutions.design.desc')}
                </div>
                <div className="text-xs text-accent font-medium">
                  {t('dashboard.customSolutions.design.link')}
                </div>
              </div>
            </div>
          </a>
          </div>
        </div>
      </div>
    </div>
  );
}

